/*
 * File: InstructorAvatars.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import type { CoursePeriod } from 'types/periods';

import { getInitials } from '../../utils';

interface InstructorAvatarsProps {
  instructors: CoursePeriod['course_period_instructors'];
}

export default function InstructorAvatars({ instructors }: InstructorAvatarsProps) {
  if (!instructors || instructors.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
        Instructor{instructors.length > 1 ? 's' : ''}
      </Typography>
      <List dense disablePadding>
        {instructors.map((coursePeriodInstructor) => {
          const instructor = coursePeriodInstructor.instructor;
          const user = instructor.user;
          const displayName = `${user.nickname || user.first_name} ${user.last_name}`;
          const initials = getInitials(user.first_name, user.last_name);
          const calendarColor = instructor.calendar_color || '#1976d2'; // Default to primary blue

          return (
            <ListItem key={instructor.user_id} disablePadding sx={{ mb: 1 }}>
              <ListItemAvatar sx={{ minWidth: 48 }}>
                <Avatar
                  sx={{
                    bgcolor: calendarColor,
                    color: 'white',
                    fontWeight: 'bold',
                    width: 40,
                    height: 40,
                  }}
                >
                  {initials}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={displayName}
                slotProps={{
                  primary: {
                    variant: 'body2',
                    fontWeight: 'medium',
                  },
                  secondary: {
                    variant: 'caption',
                    color: 'text.secondary',
                  },
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
